import cn from 'classnames'
import React from 'react'
import { useTranslate } from '../src/hooks/useTranslate'
import { ProductTypeApplicableFilters, ProductCategoryFilterRanges, ProductTypeFilterSetting } from '../src/model/fragments'
import { useIsVatPreferred } from '../src/utils/useIsVatPreferred'
import { Band } from './Band'
import { Filter, FilterProps } from './Filter'
import { ColorSelect } from './productListing/ColorSelect'
import { FeatureSelect } from './productListing/FeaturesSelect'
import { PriceRange } from './productListing/PriceRange'
import { SizeAttributeRange } from './productListing/SizeAttributeRange'
import { SurfaceSelect } from './productListing/SurfaceSelect'

const FilterIsUsefulContext = React.createContext<(isUseful: boolean) => void>(
	() => {},
)

export const useSetfilterIsUseful = () =>
	React.useContext(FilterIsUsefulContext)

const FilterItem: React.FunctionComponent<FilterProps> = (props) => {
	const [isUseful, setIsUseful] = React.useState(true)

	return (
		<FilterIsUsefulContext.Provider value={setIsUseful}>
			<div className={cn('filters-item', isUseful && 'is-useful')}>
				<Filter {...props} />
			</div>
		</FilterIsUsefulContext.Provider>
	)
}

export interface FiltersProps {
	applicable: ProductTypeApplicableFilters, 
	filter: ProductTypeFilterSetting,
	ranges: ProductCategoryFilterRanges,
	onChange: (filter: ProductTypeFilterSetting) => void,
}

export const Filters: React.FunctionComponent<FiltersProps> = ({ applicable, filter, ranges, onChange}) => {
	const translate = useTranslate();
	const [isVatPreferred, ] = useIsVatPreferred()

	if(Object.values(applicable).filter(a => a).length == 0)
	{
		return <></>;
	}

	const normalizeFilter = React.useCallback((filter: ProductTypeFilterSetting) => {
		for(const key in filter)
		{
			const k = key as keyof ProductTypeFilterSetting;
			if(!filter[k] || (typeof filter[k] === 'object' && Object.keys(filter[k] as object).length == 0) || (Array.isArray(filter[k]) && (filter[k]! as any[]).length == 0))
			{
				delete filter[k];
			}
		}

		return filter;
	}, []);

	const hasActiveFilters = React.useMemo(() => {
		const normalized = normalizeFilter(filter);
		return Object.keys(normalized).length > 0; 
	}, [filter, normalizeFilter]);

	return (
		<Band>
			<div className="filters">
				{
					applicable.color == true &&
					<FilterItem 
						label={translate('filters-color-label')} 
						expandLabel={translate('filters-color-expandLabel')}
						options={<ColorSelect selected={filter.colors ?? []} onChange={ids => onChange(normalizeFilter({...filter, colors: ids}))} colors={ranges.colors} />}
					/>
				}
				{
					applicable.surface == true &&
					<FilterItem 
						label={translate('filters-surface-label')} 
						expandLabel={translate('filters-surface-expandLabel')}
						options={<SurfaceSelect selected={filter.surfaces ?? []} onChange={ids => onChange(normalizeFilter({...filter, surfaces: ids}))} surfaces={ranges.surfaces} />}
					/>
				}
				{
					applicable.price == true &&
					<FilterItem 
						label={isVatPreferred ? translate('product-price-vat-header') : translate('product-price-noVat-header')} 
						options={<PriceRange selected={filter.priceRange} ranges={ranges.priceRange} onChange={r => onChange(normalizeFilter({...filter, priceRange: r}))}  />}
					/>
				}
				{
					applicable.length == true &&
					<FilterItem 
						label={translate('filter-length')} 
						options={<SizeAttributeRange selected={filter.lengthRange} range={ranges.lengthRange} onChange={r => onChange(normalizeFilter({...filter, lengthRange: r}))} />}
					/>
				}
				{
					applicable.width == true &&
					<FilterItem 
						label={translate('filter-width')} 
						options={<SizeAttributeRange selected={filter.widthRange} range={ranges.widthRange} onChange={r => onChange(normalizeFilter({...filter, widthRange: r}))} />}
					/>
				}
				{
					applicable.height == true &&
					<FilterItem 
						label={translate('filter-height')} 
						options={<SizeAttributeRange selected={filter.heightRange} range={ranges.heightRange} onChange={r => onChange(normalizeFilter({...filter, heightRange: r}))} />}
					/>
				}
				{
					applicable.attributes == true &&
					<FilterItem 
						label={translate('filters-attributes-label')} 
						options={<FeatureSelect setting={filter.attributes} onChange={attrs => onChange(normalizeFilter({...filter, attributes: attrs}))} />}
						expandLabel={translate('filters-attributes-expandLabel')}
					/>
				}
		

				{ hasActiveFilters && (
					<div className="filters-reset">
						<button
							type="button"
							className="filters-reset-in"
							onClick={() => onChange({} as ProductTypeFilterSetting)}
						>
							{translate('filter-reset')}
						</button>
					</div>
				)}
			</div>
		</Band>
	)
}
